import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Tab,
  Tabs,
  useTheme,
  alpha,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  FileDownload as ExportIcon,
  FilterList as FilterIcon,
  Badge as BadgeIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Block as BlockIcon,
} from '@mui/icons-material';

const Empleados = () => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const empleados = [
    {
      id: 1,
      legajo: 'PL-001',
      nombre: 'Juan',
      apellido: 'Rodríguez',
      dni: '25.456.789',
      cargo: 'Operador de Campo',
      area: 'Perforación',
      turno: '14x7',
      fechaIngreso: '15/03/2020',
      estado: 'Activo',
      aptoMedico: 'Vigente',
      artVigente: true,
    },
    {
      id: 2,
      legajo: 'PL-002',
      nombre: 'Pedro',
      apellido: 'García',
      dni: '28.789.456',
      cargo: 'Supervisor',
      area: 'Workover',
      turno: '7x7',
      fechaIngreso: '01/06/2019',
      estado: 'Activo',
      aptoMedico: 'Vencido',
      artVigente: true,
    },
    {
      id: 3,
      legajo: 'PL-003',
      nombre: 'María',
      apellido: 'López',
      dni: '30.123.456',
      cargo: 'Administrativa',
      area: 'Administración',
      turno: 'Normal',
      fechaIngreso: '10/01/2021',
      estado: 'Activo',
      aptoMedico: 'Vigente',
      artVigente: true,
    },
    {
      id: 4,
      legajo: 'PL-004',
      nombre: 'Carlos',
      apellido: 'Mendoza',
      dni: '27.654.321',
      cargo: 'Técnico HSE',
      area: 'Seguridad',
      turno: '14x7',
      fechaIngreso: '20/08/2018',
      estado: 'Licencia',
      aptoMedico: 'Vigente',
      artVigente: false,
    },
    {
      id: 5,
      legajo: 'PL-005',
      nombre: 'Ana',
      apellido: 'Martínez',
      dni: '32.987.654',
      cargo: 'Ingeniera de Producción',
      area: 'Producción',
      turno: '7x7',
      fechaIngreso: '05/04/2022',
      estado: 'Activo',
      aptoMedico: 'Por vencer',
      artVigente: true,
    },
  ];

  const columns = [
    {
      field: 'legajo',
      headerName: 'Legajo',
      width: 100,
      renderCell: (params) => (
        <Chip label={params.value} size="small" variant="outlined" color="primary" />
      ),
    },
    {
      field: 'nombreCompleto',
      headerName: 'Nombre',
      width: 180,
      valueGetter: (params) => `${params.row.nombre} ${params.row.apellido}`,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main, fontSize: '0.875rem' }}>
            {params.row.nombre[0]}{params.row.apellido[0]}
          </Avatar>
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    { field: 'dni', headerName: 'DNI', width: 120 },
    { field: 'cargo', headerName: 'Cargo', width: 180 },
    { field: 'area', headerName: 'Área', width: 130 },
    {
      field: 'turno',
      headerName: 'Turno',
      width: 100,
      renderCell: (params) => (
        <Chip label={params.value} size="small" color="info" variant="outlined" />
      ),
    },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 110,
      renderCell: (params) => {
        const color = params.value === 'Activo' ? 'success' : params.value === 'Licencia' ? 'warning' : 'error';
        return <Chip label={params.value} size="small" color={color} />;
      },
    },
    {
      field: 'aptoMedico',
      headerName: 'Apto Médico',
      width: 130,
      renderCell: (params) => {
        let icon, color;
        switch (params.value) {
          case 'Vigente':
            icon = <CheckCircleIcon fontSize="small" />;
            color = 'success';
            break;
          case 'Por vencer':
            icon = <WarningIcon fontSize="small" />;
            color = 'warning';
            break;
          default:
            icon = <BlockIcon fontSize="small" />;
            color = 'error';
        }
        return <Chip icon={icon} label={params.value} size="small" color={color} variant="outlined" />;
      },
    },
    {
      field: 'acciones',
      headerName: 'Acciones',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
            setSelectedRow(params.row);
          }}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">
            Empleados
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestión de legajos y datos del personal
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<ExportIcon />}>
            Exportar
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
            Nuevo Empleado
          </Button>
        </Box>
      </Box>

      <Card>
        <CardContent>
          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <TextField
              placeholder="Buscar empleado..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Área</InputLabel>
              <Select label="Área" defaultValue="">
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="Perforación">Perforación</MenuItem>
                <MenuItem value="Workover">Workover</MenuItem>
                <MenuItem value="Producción">Producción</MenuItem>
                <MenuItem value="Administración">Administración</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Estado</InputLabel>
              <Select label="Estado" defaultValue="">
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="Activo">Activo</MenuItem>
                <MenuItem value="Licencia">Licencia</MenuItem>
                <MenuItem value="Baja">Baja</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Turno</InputLabel>
              <Select label="Turno" defaultValue="">
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="7x7">7x7</MenuItem>
                <MenuItem value="14x7">14x7</MenuItem>
                <MenuItem value="21x7">21x7</MenuItem>
                <MenuItem value="Normal">Normal</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Tabs */}
          <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)} sx={{ mb: 2 }}>
            <Tab label="Todos (284)" />
            <Tab label="Activos (265)" />
            <Tab label="En Licencia (12)" />
            <Tab label="Apto Vencido (3)" />
          </Tabs>

          {/* Data Grid */}
          <DataGrid
            rows={empleados}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            autoHeight
            disableSelectionOnClick
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell': {
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                borderBottom: 'none',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => setAnchorEl(null)}>
          <ViewIcon fontSize="small" sx={{ mr: 1 }} /> Ver Legajo
        </MenuItem>
        <MenuItem onClick={() => setAnchorEl(null)}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Editar
        </MenuItem>
        <MenuItem onClick={() => setAnchorEl(null)} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Dar de Baja
        </MenuItem>
      </Menu>

      {/* New Employee Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BadgeIcon color="primary" />
            <Typography variant="h6">Nuevo Empleado</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Nombre" required />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Apellido" required />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="DNI" required />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="CUIL" required />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Fecha de Nacimiento" type="date" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Área</InputLabel>
                <Select label="Área">
                  <MenuItem value="Perforación">Perforación</MenuItem>
                  <MenuItem value="Workover">Workover</MenuItem>
                  <MenuItem value="Producción">Producción</MenuItem>
                  <MenuItem value="Administración">Administración</MenuItem>
                  <MenuItem value="Logística">Logística</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Cargo" required />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Turno</InputLabel>
                <Select label="Turno">
                  <MenuItem value="7x7">7x7</MenuItem>
                  <MenuItem value="14x7">14x7</MenuItem>
                  <MenuItem value="21x7">21x7</MenuItem>
                  <MenuItem value="Rotativo">Rotativo</MenuItem>
                  <MenuItem value="Normal">Normal (Oficina)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Fecha de Ingreso" type="date" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Dirección" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Teléfono" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Email" type="email" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => setOpenDialog(false)}>
            Guardar Empleado
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Empleados;
